import { keyInformationFactory } from "@/factories/pdp";
import { useQuery } from "@tanstack/react-query";

/**
 * Fetches key information data from the API route
 *
 * @param packageId - Package identifier
 * @param neoId - Neo identifier
 * @returns Promise resolving to transformed Key Information data
 */

export const fetchKeyInformationData =
  (packageId: string, neoId: string, currencyId: string, marketId: string) => async (): Promise<any> => {
    try {
      const url = `/api/pdp/key-information?packageId=${packageId}&neoId=${neoId}&currencyId=${currencyId}&marketId=${marketId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const keyInformationData = await response.json();

      return keyInformationFactory(keyInformationData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching key information data:", error); // TODO: log error to error tracking service
      throw error;
    }
  };

/**
 * React Query hook for fetching key information data
 *
 * Follows established PDP patterns for data fetching via API routes.
 * Data is cached with React Query and never goes stale (staleTime: Infinity).
 *
 * @param packageId - Package identifier
 * @param neoId - Neo identifier
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with data, isLoading, error states
 *
 * @example
 * ```tsx
 * const { data: keyInformationData, isLoading } = useKeyInformation(
 *   "16128",
 *   "686"
 * );
 * ```
 */
const useKeyInformation = (packageId: string, neoId: string, currencyId: string, marketId: string, enabled: boolean = true) =>
  useQuery<any>({
    queryKey: ["PDP_KEY_INFORMATION", neoId, packageId, currencyId, marketId],
    queryFn: fetchKeyInformationData(packageId, neoId, currencyId, marketId),
    enabled: enabled && !!currencyId && !!marketId,
    staleTime: Infinity
  });

export default useKeyInformation;
